import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listDrivers,
  getDriverDetail,
  getDriverStats,
  setDriverLinkStatus,
  listDriverPasscodes,
  type DriverRecord,
  type DriverDetailPayload,
  type DriverStats,
  type DriverPasscodeRecord,
} from "@veorest/api-client";
import { formatCurrency } from "@veorest/utils";
import {
  Bike, Car, ChevronLeft, RefreshCw, Key, Ban, CheckCircle, Star,
  Phone, Mail,
} from "lucide-react";
import { useTenant } from "../hooks/useTenant";
import { useI18n } from "../i18n";
import { cacheDrivers, useBackendReachable, queueAction } from "@veorest/pwa";

const VEHICLE_ICONS: Record<string, React.FC<{ size?: number }>> = {
  motorcycle: (p) => <Bike size={p?.size ?? 16} />,
  car: (p) => <Car size={p?.size ?? 16} />,
  bicycle: (p) => <Bike size={p?.size ?? 16} />,
};

export default function DriversPage() {
  const { t } = useI18n();
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? "";
  const currency = tenant?.currency ?? "IQD";
  const qc = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showPasscodes, setShowPasscodes] = useState(false);
  const [busy, setBusy] = useState(false);
  const reachable = useBackendReachable();

  const driversQuery = useQuery({
    queryKey: ["pos-drivers", tenantId],
    queryFn: () => listDrivers(tenantId!),
    enabled: Boolean(tenantId),
    refetchInterval: reachable ? 15_000 : false,
  });
  const statsQuery = useQuery({
    queryKey: ["pos-driver-stats", tenantId],
    queryFn: () => getDriverStats(tenantId!),
    enabled: Boolean(tenantId && reachable),
  });
  const detailQuery = useQuery({
    queryKey: ["pos-driver-detail", tenantId, selectedId],
    queryFn: () => getDriverDetail(tenantId!, selectedId!),
    enabled: Boolean(tenantId && selectedId && reachable),
  });
  const passcodesQuery = useQuery({
    queryKey: ["pos-driver-passcodes", tenantId],
    queryFn: () => listDriverPasscodes(tenantId!),
    enabled: Boolean(tenantId && showPasscodes && reachable),
  });

  const drivers: DriverRecord[] = driversQuery.data ?? [];
  const stats: DriverStats | null = statsQuery.data ?? null;
  const detail: DriverDetailPayload | null = detailQuery.data ?? null;
  const passcodes: DriverPasscodeRecord[] = passcodesQuery.data ?? [];

  // Cache drivers to IndexedDB on successful fetch
  useEffect(() => {
    if (driversQuery.data && driversQuery.data.length > 0 && tenantId) {
      cacheDrivers(tenantId, driversQuery.data.map((d) => ({
        id: d.id, tenant_id: tenantId, name: d.name ?? "",
        phone: d.phone ?? null, vehicle_type: d.vehicle_type ?? null,
        is_online: d.is_online ?? false, is_verified: d.is_verified ?? false,
        collects_cod: d.collects_cod ?? false, link_status: d.link_status ?? "active",
        cached_at: Date.now(),
      })));
    }
  }, [driversQuery.data, tenantId]);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["pos-drivers", tenantId] });
    qc.invalidateQueries({ queryKey: ["pos-driver-detail", tenantId] });
    qc.invalidateQueries({ queryKey: ["pos-driver-stats", tenantId] });
  }, [qc, tenantId]);

  const handleBlock = useCallback(async (driverId: string, blocked: boolean) => {
    if (!reachable) {
      // Optimistically update: driver link status changes handled on next sync
      queueAction("driver_block", { tenantId: tenantId!, driverId, blocked });
      return;
    }
    setBusy(true);
    try {
      await setDriverLinkStatus(tenantId!, driverId, blocked ? "blocked" : "active");
      invalidate();
    } finally { setBusy(false); }
  }, [tenantId, reachable, invalidate]);

  if (!tenantId) return <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", gap: 8, padding: "12px 16px", background: "var(--surface-raised)", borderBottom: "1px solid var(--border-subtle)", alignItems: "center", flexShrink: 0 }}>
        <span style={{ fontWeight: 800, fontSize: 16, color: "var(--text-primary)" }}>{t("drivers.title")}</span>
        {stats && (
          <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--text-muted)" }}>
            <span>{stats.total} {t("drivers.total")}</span>
            <span style={{ color: "var(--success)" }}>{stats.online} {t("drivers.online")}</span>
            <span style={{ color: "var(--warning)" }}>{stats.on_delivery} {t("drivers.delivering")}</span>
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowPasscodes(!showPasscodes)} style={{
          padding: "8px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)",
          background: showPasscodes ? "var(--accent)" : "transparent",
          color: showPasscodes ? "#fff" : "var(--text-secondary)",
          fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
        }}><Key size={14} /> {t("drivers.keys")}</button>
        <button onClick={() => { invalidate(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 8 }}>
          <RefreshCw size={18} className={driversQuery.isFetching ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Passcode management panel */}
      {showPasscodes && (
        <div style={{ padding: "12px 16px", background: "var(--surface-muted)", borderBottom: "1px solid var(--border-subtle)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
            {passcodes.length === 0 ? (
              <span style={{ color: "var(--text-muted)" }}>{t("drivers.noPasscodes")}</span>
            ) : passcodes.map((pc) => (
              <code key={pc.id} style={{
                padding: "6px 12px", borderRadius: "var(--radius-md)", background: "var(--surface-raised)",
                border: "1px solid var(--border)", fontWeight: 700, fontFamily: "monospace",
                color: pc.claimed_at ? "var(--text-muted)" : "var(--success)",
                textDecoration: pc.claimed_at ? "line-through" : "none",
              }}>
                {pc.code}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Split pane */}
      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {/* Driver list */}
        <div style={{
          width: selectedId ? "min(400px, 40%)" : "100%",
          minWidth: selectedId ? 300 : 0,
          borderRight: selectedId ? "1px solid var(--border-subtle)" : "none",
          overflowY: "auto", transition: "width 0.2s",
        }}>
          {driversQuery.isLoading ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>{t("drivers.loading")}</div>
          ) : drivers.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>
              <Bike size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontWeight: 700, fontSize: 15 }}>{t("drivers.noDrivers")}</p>
            </div>
          ) : (
            drivers.map((d) => {
              const VehIcon = VEHICLE_ICONS[d.vehicle_type] ?? Car;
              const isSelected = selectedId === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedId(isSelected ? null : d.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
                    width: "100%", border: "none", borderBottom: "1px solid var(--border-subtle)",
                    background: isSelected ? "var(--accent-light)" : "var(--surface-raised)",
                    cursor: "pointer", textAlign: "start",
                    borderLeft: isSelected ? "3px solid var(--accent)" : "3px solid transparent",
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                    background: d.link_status === "blocked" ? "#fee2e2" : d.is_online ? "#d1fae5" : "var(--surface-hover)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 16, color: d.link_status === "blocked" ? "var(--danger)" : d.is_online ? "var(--success)" : "var(--text-muted)",
                    position: "relative",
                  }}>
                    {d.name?.charAt(0) ?? "D"}
                    <span style={{
                      position: "absolute", bottom: -2, right: -2,
                      width: 12, height: 12, borderRadius: "50%",
                      background: d.link_status === "blocked" ? "#ef4444" : d.is_online ? "#22c55e" : "#9ca3af",
                      border: "2px solid white",
                    }} />
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: "var(--text-primary)" }}>{d.name}</span>
                      {d.link_status === "blocked" && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 999, background: "#fee2e2", color: "#dc2626" }}>{t("drivers.blocked")}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--text-muted)", alignItems: "center" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><VehIcon size={12} />{d.vehicle_type}</span>
                      {d.vehicle_number && <span>{d.vehicle_number}</span>}
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Star size={11} style={{ color: "#f59e0b" }} />{d.rating?.toFixed(1)}</span>
                    </div>
                  </div>
                  {/* Stats */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "var(--accent)" }}>
                      {formatCurrency(d.wallet_balance ?? 0, currency)}
                    </div>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{d.completed_deliveries} {t("drivers.trips")}</span>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        {selectedId && detail && (
          <div style={{ flex: 1, overflowY: "auto", background: "var(--surface-raised)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid var(--border-subtle)", position: "sticky", top: 0, background: "var(--surface-raised)", zIndex: 1 }}>
              <button onClick={() => setSelectedId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 4 }}>
                <ChevronLeft size={20} />
              </button>
              <span style={{ fontWeight: 800, fontSize: 16 }}>{detail.driver.name}</span>
              <span style={{
                padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                background: detail.driver.link_status === "blocked" ? "#fee2e2" : detail.driver.is_online ? "#d1fae5" : "#f3f4f6",
                color: detail.driver.link_status === "blocked" ? "#dc2626" : detail.driver.is_online ? "#065f46" : "#374151",
              }}>
                {detail.driver.link_status === "blocked" ? t("drivers.blocked") : detail.driver.is_online ? t("drivers.onlineStatus") : t("drivers.offline")}
              </span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => handleBlock(detail.driver.id, detail.driver.link_status !== "blocked")}
                disabled={busy}
                style={{
                  padding: "8px 18px", borderRadius: "var(--radius-md)", border: "none",
                  background: detail.driver.link_status === "blocked" ? "var(--success)" : "var(--danger)",
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: busy ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {detail.driver.link_status === "blocked" ? <CheckCircle size={14} /> : <Ban size={14} />}
                {detail.driver.link_status === "blocked" ? t("drivers.unblock") : t("drivers.block")}
              </button>
            </div>

            <div style={{ padding: "18px 20px", maxWidth: 700 }}>
              {/* Profile card */}
              <div style={{ marginBottom: 20, padding: 16, background: "var(--surface-muted)", borderRadius: "var(--radius-lg)", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, color: "var(--accent)", flexShrink: 0 }}>
                  {detail.driver.name?.charAt(0) ?? "D"}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 18 }}>{detail.driver.name}</p>
                  <div style={{ display: "flex", gap: 12, fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>
                    {detail.driver.phone && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Phone size={12} />{detail.driver.phone}</span>}
                    {detail.driver.email && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Mail size={12} />{detail.driver.email}</span>}
                  </div>
                </div>
              </div>

              {/* KPI grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
                {[
                  { label: t("drivers.kpi.trips"), value: detail.driver.completed_deliveries },
                  { label: t("drivers.kpi.rating"), value: (detail.driver.rating ?? 0).toFixed(1) + " ★" },
                  { label: t("drivers.kpi.acceptance"), value: (detail.driver.acceptance_rate ?? 0) + "%" },
                  { label: t("drivers.kpi.wallet"), value: formatCurrency(detail.wallet?.balance ?? 0, currency), accent: true },
                  { label: t("drivers.kpi.codPending"), value: formatCurrency(detail.driver.cod_pending_total ?? 0, currency) },
                  { label: t("drivers.kpi.feesEarned"), value: formatCurrency(detail.driver.trip_fees_total ?? 0, currency) },
                ].map((kpi) => (
                  <div key={kpi.label} style={{ padding: 12, background: "var(--surface-muted)", borderRadius: "var(--radius-md)", textAlign: "center" }}>
                    <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{kpi.label}</p>
                    <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: kpi.accent ? "var(--accent)" : "var(--text-primary)" }}>{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* Vehicle */}
              <h3 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t("drivers.vehicle")}</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20, padding: 14, background: "var(--surface-muted)", borderRadius: "var(--radius-md)" }}>
                {[
                  { label: t("drivers.type"), value: detail.driver.vehicle_type },
                  { label: t("drivers.number"), value: detail.driver.vehicle_number || "—" },
                  { label: t("drivers.model"), value: detail.driver.vehicle_model || "—" },
                  { label: t("drivers.year"), value: detail.driver.vehicle_year || "—" },
                  { label: t("drivers.color"), value: detail.driver.vehicle_color || "—" },
                ].map((v) => (
                  <div key={v.label} style={{ minWidth: 100 }}>
                    <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>{v.label}</p>
                    <p style={{ margin: "2px 0 0", fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                      {v.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Recent trips */}
              {detail.trips && detail.trips.length > 0 && (
                <>
                  <h3 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t("drivers.recentTrips")}</h3>
                  <div style={{ marginBottom: 20 }}>
                    {detail.trips.slice(0, 5).map((trip: any) => (
                      <div key={trip.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border-subtle)", fontSize: 13 }}>
                        <span style={{ fontWeight: 600 }}>Order #{trip.order_display_number ?? trip.order_id?.slice(0, 8)}</span>
                        <span style={{ color: "var(--text-muted)" }}>{trip.status ?? "completed"}</span>
                        <span style={{ fontWeight: 700 }}>{trip.amount ? formatCurrency(trip.amount, currency) : "—"}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Meta */}
              <div style={{ padding: 12, background: "var(--surface-muted)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", gap: 20 }}>
                <span>{t("drivers.meta.joined")}: <strong style={{ color: "var(--text-primary)" }}>{new Date(detail.driver.link_created_at ?? detail.driver.created_at).toLocaleDateString()}</strong></span>
                <span>{t("drivers.meta.verified")}: <strong style={{ color: detail.driver.is_verified ? "var(--success)" : "var(--warning)" }}>{detail.driver.is_verified ? t("drivers.meta.yes") : t("drivers.meta.no")}</strong></span>
                <span>{t("drivers.meta.cod")}: <strong style={{ color: detail.driver.collects_cod ? "var(--success)" : "var(--text-muted)" }}>{detail.driver.collects_cod ? t("drivers.meta.yes") : t("drivers.meta.no")}</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
